import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { useDispatch } from "react-redux";
import cn from "classnames";

import { addContacts } from "actions/addContacts";
import { deleteContact } from "actions/deleteContact";
import { toggleBlockContact } from "actions/toggleBlockContact";

import styles from "./styles.module.scss";

export const DIRECTION = {
    RIGHT_TOP: "right-top",
    LEFT_BOTTOM: "left-bottom",
};

const ANIMATION_TIMEOUT = 400;

export default function ChatMenu({
    isOpen,
    onCloseMenu,
    contact,
    direction = DIRECTION.RIGHT_TOP,
    createConversation,
    setIsUserBlocked,
    setIsUserContact,
    usePortal = false,
    getButtonRect,
    menuRef,
}) {
    const { employee } = contact;
    const [isLoading, setIsLoading] = useState(false);
    const nodeRef = useRef(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const handleAddToContacts = useCallback(() => {
        setIsLoading(true);
        dispatch(addContacts(employee.id)).then(() => {
            setIsUserContact && setIsUserContact(true);
            onCloseMenu();
            setIsLoading(false);
        });
    }, [dispatch, onCloseMenu, employee.id, setIsUserContact]);

    const handleDeleteContact = useCallback(() => {
        setIsLoading(true);
        dispatch(deleteContact(employee.id)).then(() => {
            setIsUserContact && setIsUserContact(false);
            onCloseMenu();
            setIsLoading(false);
        });
    }, [dispatch, employee, onCloseMenu, setIsUserContact]);

    const toggleBlockUser = async () => {
        createConversation && (await createConversation());
        setIsLoading(true);
        dispatch(toggleBlockContact(employee)).then(() => {
            setIsUserBlocked && setIsUserBlocked(!employee.isBlock);
            onCloseMenu();
            setIsLoading(false);
        });
    };

    const animationClassNames = {
        enter: styles["menu--enter"],
        enterActive: styles["menu--enter-active"],
        exit: styles["menu--exit"],
        exitActive: styles["menu--exit-active"],
    };

    const menuItems = () => {
        const actions = [];
        if (employee.isContact) {
            actions.push({
                title: t("dialog.deleteContact"),
                action: handleDeleteContact,
            });
        } else {
            actions.push({
                title: t("dialog.addToContacts"),
                action: handleAddToContacts,
            });
        }
        if (employee.isBlock) {
            actions.push({
                title: t("dialog.unBlockUser"),
                action: toggleBlockUser,
            });
        } else {
            actions.push({
                title: t("dialog.blockUser"),
                action: toggleBlockUser,
            });
        }

        return actions;
    };

    // Сохраняем последнюю позицию для анимации закрытия
    const [lastMenuStyle, setLastMenuStyle] = useState(null);

    // Вычисляем позицию только когда меню открыто
    const currentStyle = useMemo(() => {
        if (!usePortal || !getButtonRect || !isOpen) return null;

        const rect = getButtonRect();
        if (!rect) return null;

        if (direction === DIRECTION.LEFT_BOTTOM) {
            return {
                position: 'fixed',
                top: rect.bottom + 5,
                right: window.innerWidth - rect.right,
            };
        }
        // RIGHT_TOP
        return {
            position: 'fixed',
            bottom: window.innerHeight - rect.top + 5,
            left: rect.left,
        };
    }, [usePortal, getButtonRect, isOpen, direction]);

    // Сохраняем позицию когда меню открывается
    useEffect(() => {
        if (currentStyle) {
            setLastMenuStyle(currentStyle);
        }
    }, [currentStyle]);

    // Используем текущую позицию или сохранённую (для анимации закрытия)
    const menuStyle = currentStyle || lastMenuStyle;

    // Для портала не показываем меню пока нет координат
    if (usePortal && !menuStyle) {
        return null;
    }

    const menuContent = (
        <CSSTransition
            in={isOpen}
            classNames={animationClassNames}
            timeout={ANIMATION_TIMEOUT}
            className={cn(styles.menu, usePortal ? styles['menu--portal'] : styles[`menu--${direction}`])}
            unmountOnExit
            nodeRef={nodeRef}
        >
            <div ref={(el) => { nodeRef.current = el; if (menuRef) menuRef.current = el; }} style={usePortal && menuStyle ? menuStyle : undefined}>
                <ul className={styles.menu__list}>
                    {menuItems().map((item, index) => (
                        <li className={styles.menu__item} key={index}>
                            <button
                                className={styles.menu__action}
                                onClick={item.action}
                                type="button"
                                disabled={isLoading}
                            >
                                {item.title}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </CSSTransition>
    );

    if (usePortal) {
        return createPortal(menuContent, document.body);
    }

    return menuContent;
}
